import { APIRoutes } from "@/api";
import { Button } from "@/components/ui/button";
import { Intervals } from "@/constants";
import { MeQuery } from "@/hooks/queries/user/use-me-query";
import { firebaseCloudMessaging } from "@/lib/notifications/fcm";
import { urlB64ToUint8Array } from "@/utils/urlB64ToUint8Array";
import { MessagePayload } from "firebase/messaging";
import { BellIcon } from "lucide-react";
import { useSession } from "next-auth/react";
import React, { PropsWithChildren, createContext } from "react";
import { toast } from "sonner";
import { z } from "zod/v4";

const notificationSchema = z.object({
  title: z.string(),
  body: z.string(),
  icon: z.string().optional(),
  image: z.string().optional(),
});

const vapidKeyResponseSchema = z.object({
  vapidKey: z.string(),
});

async function getVapidKey() {
  const res = await fetch(APIRoutes.PUSH_VAPID_KEY);
  const data = await res.json();

  return urlB64ToUint8Array(vapidKeyResponseSchema.parse(data).vapidKey);
}

async function pushSubscribe(subscription: PushSubscription) {
  const res = await fetch(APIRoutes.PUSH_SUBSCRIBE, {
    method: "POST",
    headers: {
      "Content-type": "application/json",
    },
    body: JSON.stringify({
      subscription: subscription,
    }),
  });

  return res.status === 2000;
}

async function pushUnsubscribe(subscription: PushSubscription) {
  const res = await fetch(APIRoutes.PUSH_UNSUBSCRIBE, {
    method: "DELETE",
    headers: {
      "Content-type": "application/json",
    },
    body: JSON.stringify({
      subscription: subscription,
    }),
  });

  return res.status === 200;
}

async function registerServiceWorker(
  isPushEnabled: boolean,
  serviceWorkerPath = "/firebase-messaging-sw.js",
) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return;
  }

  if (!isPushEnabled) {
    return;
  }

  const registerSubscription = async () => {
    // Here's the waiting point for the service worker to be ready.
    const registration = await navigator.serviceWorker.ready;

    if (!registration) {
      console.log("[PushProvider] Could not get SW registration");
      return;
    }

    let subscription;
    const existingSubscription =
      await registration.pushManager.getSubscription();

    if (existingSubscription) {
      subscription = existingSubscription;
    } else {
      const vapidKey = await getVapidKey();
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });
    }

    console.log("[PushProvider] User is subscribed");

    await pushSubscribe(subscription).catch((err) => {
      console.log(
        "[PushProvider] 🚨 Error during subscription registration",
        err,
      );
    });
  };

  try {
    // await navigator.serviceWorker.register(serviceWorkerPath, {
    //   updateViaCache: "none",
    //   type: "module",
    //   scope: "/",
    // });
    let permission = Notification.permission;

    if (permission !== "granted") {
      const toastId = toast(
        <div className="flex flex-col gap-3">
          <div className="flex flex-col items-center gap-2 md:flex-row">
            <BellIcon className="size-6" />
            <p>
              You need to enable notifications to receive updates when a
              portfolio in watchlist changes.
            </p>
          </div>
          <div className="flex flex-col justify-center md:flex-row">
            <div className="flex flex-col gap-2 md:flex-row-reverse md:gap-4">
              {" "}
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  const result = await Notification.requestPermission();
                  if (result !== "granted") {
                    toast.error(
                      "You must enable the notification permission. Please enable it from your browser settings.",
                      {
                        id: toastId,
                        duration: Intervals.SECOND * 3,
                        dismissible: true,
                      },
                    );
                  } else {
                    toast.success("Permission granted. Reloading...", {
                      id: toastId,
                      duration: Intervals.SECOND * 3,
                      dismissible: true,
                      onAutoClose: () => {
                        window.location.reload();
                      },
                      onDismiss: () => {
                        window.location.reload();
                      },
                    });
                  }
                }}
              >
                Enable
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  toast.dismiss(toastId);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>,
        {
          duration: Intervals.SECOND * 10,
          dismissible: false,
        },
      );

      return;
    } else {
      await registerSubscription();
    }
  } catch (error) {
    console.error("🚨 Error during service worker registration:", error);
  }
}

export async function removePushRegistration() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    if (!registration) {
      console.log("[PushProvider] Could not get SW registration");
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      console.log("[PushProvider] Could not get subscription");
      return;
    }
    // Unsubscribe from push notifications.
    const unsubscribed = await subscription.unsubscribe();
    if (!unsubscribed) {
      console.log(
        "[PushProvider] Unable to unsubscribe from push notifications.",
      );
    }

    await pushUnsubscribe(subscription).catch((err) => {
      console.log("[PushProvider] 🚨 Error during subscription removal", err);
    });
  } catch (error) {
    console.error("🚨 Error during push notification removal:", error);
  }
}

type PushProviderContextType = null;

const PushProviderContext = createContext<PushProviderContextType | undefined>(
  undefined,
);

export const PushProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const mountedRef = React.useRef(false);
  const { data: session } = useSession();
  const meQuery = MeQuery.useQuery({
    enabled: Boolean(session?.user),
  });

  const displayNotification = React.useCallback(
    ({ notification }: MessagePayload) => {
      const notificationResult = notificationSchema.safeParse(notification);
      if (!notificationResult.success) {
        return;
      }

      const data = notificationResult.data;

      toast.info(data.title, {
        description: data.body,
        duration: Intervals.SECOND * 4,
      });
    },
    [],
  );
  if (mountedRef.current) {
    firebaseCloudMessaging.onMessage(displayNotification);
  }

  React.useEffect(() => {
    if (!session?.user.id || !meQuery.data?.pushPreferences?.enabled) {
      return;
    }

    const bootstrapAsync = async () => {
      await firebaseCloudMessaging.init();

      const setToken = async () => {
        const token = await firebaseCloudMessaging.tokenInlocalforage();
        if (token) {
          mountedRef.current = true;
        }
      };
      await setToken();
    };

    bootstrapAsync();
  }, [session?.user.id, meQuery.data?.pushPreferences?.enabled]);

  React.useEffect(() => {
    if (!session?.user.id || !meQuery.data?.pushPreferences?.enabled) {
      return;
    }

    const isPushEnabled = meQuery.data.pushPreferences.enabled;

    registerServiceWorker(isPushEnabled);
  }, [session?.user.id, meQuery.data?.pushPreferences?.enabled]);

  return (
    <PushProviderContext.Provider value={null}>
      {children}
    </PushProviderContext.Provider>
  );
};
